<template>
  <v-card v-if="loading">
    <v-skeleton-loader type="image" />
  </v-card>

  <v-card v-else flat class="card-bars-stack d-flex justify-center">
    <v-card-text>
      <div v-if="isEmpty">
        <p>{{ $t('honda.dashboard.stats.no-data') }}</p>
      </div>

      <div v-else>
        <span v-if="status === 'on_track'" class="card-goal on-track">
          <v-icon>fa-light fa-check</v-icon>
        </span>

        <span v-if="status === 'needs_push'" class="card-goal needs-push">
          <v-icon>fa-light fa-triangle-exclamation</v-icon>
        </span>

        <div class="card-bars-stack-metric">
          <p :class="`metric-title ${status ? 'max-width-90' : ''}`">{{ title }}</p>
        </div>

        <div class="card-graph-wrapper">
          <div class="card-bars-stack-graph">
            <div class="current-value">
              <p>{{ sales }}</p>
              <p>{{ $t('honda.dashboard.stats.pre-deliveries.units') }}</p>
            </div>
            <v-chart ref="chart" :option="options" :autoresize="true" />
          </div>

          <div class="card-bars-stack-graph">
            <div class="current-value">
              <p>{{ weight }}</p>
              <p>{{ $t('honda.dashboard.stats.pre-deliveries.weight') }}</p>
            </div>
            <v-chart ref="chart" :option="optionsWeight" :autoresize="true" />
          </div>
        </div>

        <p v-if="lastData" class="note">
          {{ $t('honda.dashboard.stats.pre-deliveries.last-data', {lastData}) }}
        </p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import {computed, defineComponent, toRefs} from '@vue/composition-api'
import {DateTime} from 'luxon'

import {useI18n} from '@/i18n'

export default defineComponent({
  name: 'CardBarsStack',
  props: {
    loading: {
      type: Boolean,
      required: false,
    },
    title: {
      type: String,
      required: true,
    },
    sales: {
      type: Number,
      required: false,
    },
    salesTarget100: {
      type: Number,
      required: false,
    },
    salesTarget105: {
      type: Number,
      required: false,
    },
    weight: {
      type: Number,
      required: false,
    },
    weightTarget: {
      type: Number,
      required: false,
    },
    status: {
      type: String,
      required: false,
    },
    lastDelivery: {
      type: String,
      required: false,
      default: '',
    },
  },
  setup(props) {
    const {i18n} = useI18n()
    const {sales, salesTarget100, salesTarget105, weight, weightTarget, lastDelivery} = toRefs(props)

    const isEmpty = computed(() => {
      return (!sales?.value && sales?.value !== 0) || (!salesTarget100?.value && salesTarget100?.value !== 0)
    })

    const lastData = computed(() => {
      const date = DateTime.fromISO(lastDelivery.value)

      if (!date.isValid) {
        return
      }

      return date.toLocaleString(DateTime.DATE_FULL, {locale: i18n.locale})
    })

    const options = computed(() => {
      const value = sales?.value || 0
      const limit = salesTarget100?.value ? salesTarget100.value * 2 : 0
      const overload = value >= limit

      const bar1 = overload ? 1 : value / limit
      const bar2 = overload ? 0 : (limit - value) / limit

      return {
        color: ['#dc192d', '#eee', '#eee'],
        grid: {
          top: 'center',
          left: 'center',
          width: '100%',
        },
        xAxis: {
          type: 'value',
          show: false,
        },
        yAxis: {
          type: 'category',
          data: ['completed'],
          inverse: true,
          axisLine: {show: false},
          axisTick: {show: false},
          axisLabel: {show: false},
        },
        series: [
          {
            type: 'bar',
            stack: 'total',
            silent: true,
            barWidth: 25,
            data: [bar1],
            label: {show: false},
          },
          {
            type: 'bar',
            stack: 'total',
            silent: true,
            barWidth: 25,
            data: [bar2],
            label: {show: false},
            markLine: {
              silent: true,
              symbol: 'none',
              lineStyle: {
                color: '#000',
                type: 'solid',
              },
              data: [
                {
                  xAxis: 1 / 2,
                  label: {
                    position: 'start',
                    align: 'right',
                    formatter: () => `${salesTarget100?.value} (100%)`,
                  },
                },
                salesTarget105?.value
                  ? {
                      xAxis: 1.05 / 2,
                      label: {
                        position: 'end',
                        align: 'left',
                        formatter: () => `${salesTarget105.value} (105%)`,
                      },
                    }
                  : null,
              ].filter(axis => axis),
            },
          },
        ],
      }
    })

    const optionsWeight = computed(() => {
      const value = weight?.value || 0
      const limit = weightTarget?.value ? weightTarget.value * 2 : 0
      const overload = value >= limit

      const bar1 = overload ? 1 : value / limit
      const bar2 = overload ? 0 : (limit - value) / limit

      return {
        color: ['#dc192d', '#eee', '#eee'],
        grid: {
          top: 'center',
          left: 'center',
          width: '100%',
        },
        xAxis: {
          type: 'value',
          show: false,
        },
        yAxis: {
          type: 'category',
          data: ['completed'],
          inverse: true,
          axisLine: {show: false},
          axisTick: {show: false},
          axisLabel: {show: false},
        },
        series: [
          {
            type: 'bar',
            stack: 'total',
            silent: true,
            barWidth: 25,
            data: [bar1],
            label: {show: false},
          },
          {
            type: 'bar',
            stack: 'total',
            silent: true,
            barWidth: 25,
            data: [bar2],
            label: {show: false},
            markLine: {
              symbol: 'none',
              lineStyle: {
                color: '#000',
                type: 'solid',
              },
              data: [
                {
                  xAxis: 0.5,
                  label: {
                    show: true,
                    formatter: () => `${weightTarget?.value || 0}`,
                  },
                },
              ],
            },
          },
        ],
      }
    })

    return {
      isEmpty,
      lastData,
      options,
      optionsWeight,
    }
  },
})
</script>

<style>
.v-skeleton-loader__image {
  height: 270px;
}

.max-width-90 {
  max-width: 90%;
}
</style>

<style lang="scss" scoped>
.card-bars-stack {
  width: 100%;
  height: 270px;
  border-radius: 6px !important;
  box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.16) !important;
  overflow: hidden;
}

.metric-title {
  color: #222;
  font-family: 'ProximaNovaExCnSBold', system-ui, sans-serif;
  font-size: 20px;
  line-height: 1;
  text-transform: uppercase;
  margin-bottom: 2px;
}

.metric-subtitle {
  color: #aaa;
  font-family: 'ProximaNovaRegular', system-ui, sans-serif;
  font-size: 14px;
}

.metric-value {
  display: block;
  color: #222;
  font-family: 'ProximaNovaSemibold', system-ui, sans-serif;
  font-size: 32px;
  line-height: 1;
  margin-bottom: 5px;
}

.card-goal {
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 2;
  width: 40px;
  height: 40px;
  border-radius: 20px;

  &.on-track {
    background-color: #00803f;
  }

  &.needs-push {
    background-color: #fca400;
  }

  > i {
    color: white;
    font-size: 17px;
    padding-top: 14px;
    padding-left: 9px;
  }
}

.card-graph-wrapper {
  display: flex;
  flex-direction: column;
  padding: 12px;
}

.card-bars-stack-graph {
  display: flex;
  align-items: center;
  gap: 15px;
  height: 82px;
}

.current-value {
  flex: 0 0 70px;

  > p {
    font-family: 'ProximaNovaSemibold', system-ui, sans-serif;
    color: #222;
    line-height: 1;
    margin-bottom: 0;

    &:first-child {
      font-size: 32px;
    }

    &:last-child {
      font-size: 16px;
    }
  }
}

.note {
  color: #aaa;
  font-family: 'ProximaNovaRegular', system-ui, sans-serif;
  font-size: 14px;
  text-align: center;
  margin-bottom: 0;
}
</style>
